import React, { useState, useRef, useEffect, useMemo } from "react";
import Chip from "../../Chip/Chip";

const CustomTags = ({
  value = [],
  onChange,
  placeholder = "Enter tag...",
  size = "md",
  rounded = "rounded",
  disabled = false,
  maxCount = 10,
  suggestions = [],
  shadow = false,
  validate = null,
  // Function to validate the entered tag value.
  // Should return:
  //   true  → if the value is valid
  //   "error message" → if validation fails
  //
  // Example:
  // validate={(tag) => {
  //   if (tag.length < 2) return "Minimum 2 characters required";
  //   return true;
  // }}

  allowedPattern = null,
  // RegExp used to restrict characters while typing.
  // Only characters matching this pattern will be allowed.
  //
  // Example:
  // allowedPattern={/^[a-zA-Z\s]*$/}
  // Allows only letters and spaces.
}) => {
  const [inputValue, setInputValue] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [validationError, setValidationError] = useState("");
  const inputRef = useRef(null);
  const suggestionRef = useRef(null);

  const tags = useMemo(() => {
    if (!Array.isArray(value)) {
      if (typeof value === "string") return value ? [value] : [];
      return [];
    }
    return value.filter((v) => typeof v === "string");
  }, [value]);

  const filteredSuggestions = useMemo(() => {
    if (inputValue.trim() === "") {
      return [];
    }
    const lower = inputValue.toLowerCase();
    return suggestions.filter(
      (s) =>
        s.toLowerCase().includes(lower) &&
        !value.some((v) => v.toLowerCase() === s.toLowerCase()),
    );
  }, [inputValue, suggestions, value]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        suggestionRef.current &&
        !suggestionRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleInputChange = (e) => {
    if (disabled) return;
    const newVal = e.target.value;

    // Block characters that don't match the allowed pattern while typing
    if (allowedPattern && newVal && !allowedPattern.test(newVal)) return;

    setValidationError("");
    setInputValue(newVal);
    setShowSuggestions(true);
  };

  const isDuplicate = (tag) => {
    return tags.some((v) => v.toLowerCase() === tag.toLowerCase());
  };

  const handleSelectSuggestion = (tag) => {
    if (disabled || isDuplicate(tag) || tags.length >= maxCount) {
      setInputValue("");
      setShowSuggestions(false);
      return;
    }
    const newTags = [...tags, tag];
    onChange && onChange(newTags);
    setInputValue("");
    setShowSuggestions(false);
  };

  const handleKeyDown = (e) => {
    if (disabled) return;
    if (e.key === "Enter" && inputValue.trim()) {
      e.preventDefault();
      handleCreateTag();
    } else if (e.key === "Backspace" && !inputValue && tags.length > 0) {
      const newTags = tags.slice(0, -1);
      onChange && onChange(newTags);
    }
  };

  const handleCreateTag = () => {
    const tag = inputValue.trim();
    if (!tag) return;
    if (isDuplicate(tag)) {
      setValidationError(`"${tag}" is already added.`);
      setInputValue("");
      return;
    }
    if (tags.length >= maxCount) {
      setInputValue("");
      return;
    }
    // Run custom validator if provided
    if (validate) {
      const result = validate(tag);
      if (result !== true) {
        setValidationError(result || "Invalid tag.");
        return; // keep inputValue so user can fix it
      }
    }
    const newTags = [...tags, tag];
    onChange && onChange(newTags);
    setInputValue("");
    setValidationError("");
    setShowSuggestions(false);
  };

  const handleDeleteTag = (tagToDelete) => {
    if (disabled) return;
    const newTags = tags.filter((t) => t !== tagToDelete);
    onChange && onChange(newTags);
  };

  const handleInputFocus = () => {
    if (disabled) return;
    if (inputValue.trim() !== "") {
      setShowSuggestions(true);
    }
  };

  const getSizeStyles = () => {
    switch (size) {
      case "sm":
        return "px-2 py-1 text-xs";
      case "lg":
        return "px-4 py-3 text-base";
      case "md":
      default:
        return "px-3 py-2 text-sm";
    }
  };

  const getRoundedStyles = () => {
    switch (rounded) {
      case "none":
        return "rounded-none";
      case "sm":
        return "rounded-sm";
      case "md":
        return "rounded-md";
      case "lg":
        return "rounded-lg";
      case "xl":
        return "rounded-xl";
      case "full":
        return "rounded-full";
      case "rounded":
      default:
        return "rounded-md";
    }
  };

  return (
    <div className="relative w-full">
      <div
        className={`flex flex-wrap items-center border gap-1.5 ${getSizeStyles()} ${getRoundedStyles()}
        ${
          shadow
            ? "border-gray-50 shadow-[0_0_4px_rgba(71,71,71,0.25)]"
            : "border-gray-300"
        }
        ${
          disabled
            ? `bg-neutrals-200 cursor-not-allowed border-neutrals-200 text-black`
            : "bg-white hover:border-1 hover:border-primary focus-within:border-primary"
        }`}
      >
        {tags.map((tag) => (
          <Chip
            variant="outlined"
            key={tag}
            label={tag}
            onDelete={disabled ? undefined : () => handleDeleteTag(tag)}
            disabled={disabled}
            size="xs"
            className="bg-gray-50 !text-black border-info-100 !rounded !py-0"
          />
        ))}
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleInputFocus}
          disabled={disabled || tags.length >= maxCount}
          placeholder={
            tags.length === 0
              ? placeholder
              : tags.length >= maxCount
                ? `Maximum ${maxCount} tags allowed`
                : ""
          }
          className={`flex-1 min-w-[120px] border-none outline-none bg-transparent ${
            size === "sm" ? "text-xs" : size === "lg" ? "text-base" : "text-sm"
          } ${disabled || tags.length >= maxCount ? "cursor-not-allowed" : ""}`}
        />
      </div>

      {/* Validation error message */}
      {validationError && (
        <p className="mt-1 text-xs text-red-500">{validationError}</p>
      )}

      {!disabled && showSuggestions && filteredSuggestions.length > 0 && (
        <div
          className="absolute top-full left-0 w-full max-w-[350px] max-h-[200px] overflow-y-auto bg-white border border-gray-300 rounded-md z-10 mt-1 shadow-lg"
          ref={suggestionRef}
        >
          {filteredSuggestions.map((s, idx) => (
            <div
              key={`${s}-${idx}`}
              className="flex items-center p-2 cursor-pointer hover:bg-gray-100 transition-colors"
              onClick={() => handleSelectSuggestion(s)}
            >
              <div className="text-sm font-medium">{s}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CustomTags;
